import * as XLSX from 'xlsx';

/** Matches backend PDF header normalization (spaces/symbols stripped). */
export function normalizeStatementColumnKey(col) {
  return String(col == null ? '' : col)
    .replace(/[\s&_-]+/g, '')
    .toLowerCase();
}

const DATETIME_COL_KEYS = new Set([
  'completiontime',
  'transactiondate',
  'valuedate',
  'datetime',
  'initiationtime',
  'postingdate',
  'bookdate',
  'effectivedate',
]);

const AMOUNT_COL_KEYS = new Set([
  'paidin',
  'withdrawn',
  'balance',
  'moneyin',
  'moneyout',
  'credit',
  'debit',
  'ledgerbalance',
  'amount',
  'fee',
  'fees',
  'charges',
  'commission',
  'cost',
  'tax',
]);

function parseAmount(val) {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  let s = String(val).trim().replace(/\r?\n/g, ' ');
  if (!s || s === '-' || s === '—') return null;
  let neg = false;
  if (s.startsWith('(') && s.endsWith(')')) {
    neg = true;
    s = s.slice(1, -1).trim();
  }
  s = s.replace(/ksh|kes/gi, '').replace(/,/g, '').trim();
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

function parseDateOnlyString(val) {
  if (val === null || val === undefined || val === '') return null;
  const t = String(val).trim();
  if (!t) return null;
  const dm = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(t);
  if (dm) {
    const d = new Date(Number(dm[3]), Number(dm[2]) - 1, Number(dm[1]), 12, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    const d = new Date(`${t.slice(0, 10)}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** DD.MM.YYYY [HH:MM:SS] or ISO-ish strings from API */
function parseDateTimeFlexible(val) {
  if (val === null || val === undefined || val === '') return null;
  if (val instanceof Date && !Number.isNaN(val.getTime())) return val;
  const s = String(val).trim().replace('T', ' ');
  if (!s) return null;
  const dm = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(s);
  if (dm) {
    const d = new Date(
      Number(dm[3]),
      Number(dm[2]) - 1,
      Number(dm[1]),
      dm[4] != null ? Number(dm[4]) : 0,
      dm[5] != null ? Number(dm[5]) : 0,
      dm[6] != null ? Number(dm[6]) : 0,
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseTimeFraction(val) {
  if (val === null || val === undefined || val === '') return null;
  const t = String(val).trim();
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  const sec = m[3] != null ? Number(m[3]) : 0;
  if (h >= 24 || mi >= 60 || sec >= 60) return null;
  return (h * 3600 + mi * 60 + sec) / 86400;
}

function coerceAnalysisExportCell(columnName, raw) {
  const norm = normalizeStatementColumnKey(columnName);

  if (norm === 'date') {
    const d = parseDateOnlyString(raw);
    if (d != null) return d;
    return raw === '' || raw == null ? undefined : String(raw);
  }

  if (norm === 'time') {
    const frac = parseTimeFraction(raw);
    if (frac != null) {
      return { t: 'n', v: frac, z: 'hh:mm:ss' };
    }
    return raw === '' || raw == null ? undefined : String(raw);
  }

  if (DATETIME_COL_KEYS.has(norm)) {
    const d = parseDateTimeFlexible(raw);
    if (d != null) return d;
    return raw === '' || raw == null ? undefined : String(raw);
  }

  if (AMOUNT_COL_KEYS.has(norm)) {
    const n = parseAmount(raw);
    if (n != null) return { t: 'n', v: n, z: '#,##0.00' };
    return raw === '' || raw == null ? undefined : String(raw);
  }

  if (raw === null || raw === undefined) return '';
  return raw;
}

/**
 * Rows aligned with exportColumns; header row stays plain strings.
 * Uses Excel Date cells + numeric amounts when parsing succeeds.
 */
export function mapRowsForTypedExcelExport(exportColumns, exportRows) {
  return exportRows.map((row) =>
    exportColumns.map((col, i) => coerceAnalysisExportCell(col, row[i])),
  );
}

export function typedTransactionsSheet(exportColumns, exportRows) {
  const body = mapRowsForTypedExcelExport(exportColumns, exportRows);
  return XLSX.utils.aoa_to_sheet([exportColumns, ...body], {
    cellDates: true,
  });
}

/** Top entities: numeric totals and percentages */
export function typedTopEntitiesSheet(topEntitiesRows) {
  const body = topEntitiesRows.map((row) => {
    const name = row[0];
    const total = typeof row[1] === 'number' ? row[1] : parseAmount(row[1]);
    let pct = row[2];
    if (typeof pct === 'string') {
      pct = Number(String(pct).replace(/,/g, ''));
    }
    const rowOut = [name];
    rowOut.push(
      total != null && Number.isFinite(total)
        ? { t: 'n', v: total, z: '#,##0.00' }
        : row[1],
    );
    rowOut.push(
      pct != null && Number.isFinite(pct)
        ? { t: 'n', v: pct, z: '0.00' }
        : row[2],
    );
    return rowOut;
  });
  return XLSX.utils.aoa_to_sheet(
    [['Entity Name', 'Total Amount', 'Percentage (%)'], ...body],
    { cellDates: true },
  );
}
